import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Settings as SettingsIcon, Globe, Check, Search, Lock, Eye, EyeOff, X, CalendarClock } from "lucide-react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";

import { API } from '../utils/api';

const CURRENCIES = [
  // Major Currencies
  { value: "EUR", label: "Euro", symbol: "€", region: "Europe" },
  { value: "USD", label: "US Dollar", symbol: "$", region: "Americas" },
  { value: "GBP", label: "British Pound", symbol: "£", region: "Europe" },
  { value: "JPY", label: "Japanese Yen", symbol: "¥", region: "Asia" },
  { value: "CHF", label: "Swiss Franc", symbol: "CHF", region: "Europe" },
  { value: "CAD", label: "Canadian Dollar", symbol: "C$", region: "Americas" },
  { value: "AUD", label: "Australian Dollar", symbol: "A$", region: "Oceania" },
  { value: "NZD", label: "New Zealand Dollar", symbol: "NZ$", region: "Oceania" },
  // European
  { value: "SEK", label: "Swedish Krona", symbol: "kr", region: "Europe" },
  { value: "NOK", label: "Norwegian Krone", symbol: "kr", region: "Europe" },
  { value: "DKK", label: "Danish Krone", symbol: "kr", region: "Europe" },
  { value: "PLN", label: "Polish Zloty", symbol: "zł", region: "Europe" },
  { value: "CZK", label: "Czech Koruna", symbol: "Kč", region: "Europe" },
  { value: "HUF", label: "Hungarian Forint", symbol: "Ft", region: "Europe" },
  { value: "RON", label: "Romanian Leu", symbol: "lei", region: "Europe" },
  { value: "BGN", label: "Bulgarian Lev", symbol: "лв", region: "Europe" },
  { value: "HRK", label: "Croatian Kuna", symbol: "kn", region: "Europe" },
  { value: "ISK", label: "Icelandic Króna", symbol: "kr", region: "Europe" },
  // Asian
  { value: "CNY", label: "Chinese Yuan", symbol: "¥", region: "Asia" },
  { value: "HKD", label: "Hong Kong Dollar", symbol: "HK$", region: "Asia" },
  { value: "SGD", label: "Singapore Dollar", symbol: "S$", region: "Asia" },
  { value: "TWD", label: "Taiwan Dollar", symbol: "NT$", region: "Asia" },
  { value: "KRW", label: "South Korean Won", symbol: "₩", region: "Asia" },
  { value: "INR", label: "Indian Rupee", symbol: "₹", region: "Asia" },
  { value: "IDR", label: "Indonesian Rupiah", symbol: "Rp", region: "Asia" },
  { value: "MYR", label: "Malaysian Ringgit", symbol: "RM", region: "Asia" },
  { value: "PHP", label: "Philippine Peso", symbol: "₱", region: "Asia" },
  { value: "THB", label: "Thai Baht", symbol: "฿", region: "Asia" },
  { value: "VND", label: "Vietnamese Dong", symbol: "₫", region: "Asia" },
  { value: "PKR", label: "Pakistani Rupee", symbol: "₨", region: "Asia" },
  { value: "BDT", label: "Bangladeshi Taka", symbol: "৳", region: "Asia" },
  { value: "LKR", label: "Sri Lankan Rupee", symbol: "Rs", region: "Asia" },
  // Middle East & Africa
  { value: "AED", label: "UAE Dirham", symbol: "د.إ", region: "Middle East" },
  { value: "SAR", label: "Saudi Riyal", symbol: "﷼", region: "Middle East" },
  { value: "QAR", label: "Qatari Riyal", symbol: "ر.ق", region: "Middle East" },
  { value: "KWD", label: "Kuwaiti Dinar", symbol: "د.ك", region: "Middle East" },
  { value: "BHD", label: "Bahraini Dinar", symbol: "ب.د", region: "Middle East" },
  { value: "OMR", label: "Omani Rial", symbol: "ر.ع.", region: "Middle East" },
  { value: "ILS", label: "Israeli Shekel", symbol: "₪", region: "Middle East" },
  { value: "TRY", label: "Turkish Lira", symbol: "₺", region: "Middle East" },
  { value: "EGP", label: "Egyptian Pound", symbol: "E£", region: "Africa" },
  { value: "ZAR", label: "South African Rand", symbol: "R", region: "Africa" },
  { value: "NGN", label: "Nigerian Naira", symbol: "₦", region: "Africa" },
  { value: "KES", label: "Kenyan Shilling", symbol: "KSh", region: "Africa" },
  { value: "MAD", label: "Moroccan Dirham", symbol: "د.م.", region: "Africa" },
  { value: "GHS", label: "Ghanaian Cedi", symbol: "₵", region: "Africa" },
  // Americas
  { value: "MXN", label: "Mexican Peso", symbol: "MX$", region: "Americas" },
  { value: "BRL", label: "Brazilian Real", symbol: "R$", region: "Americas" },
  { value: "ARS", label: "Argentine Peso", symbol: "AR$", region: "Americas" },
  { value: "CLP", label: "Chilean Peso", symbol: "CL$", region: "Americas" },
  { value: "COP", label: "Colombian Peso", symbol: "CO$", region: "Americas" },
  { value: "PEN", label: "Peruvian Sol", symbol: "S/", region: "Americas" },
  { value: "UYU", label: "Uruguayan Peso", symbol: "$U", region: "Americas" },
  // Other
  { value: "RUB", label: "Russian Ruble", symbol: "₽", region: "Europe" },
  { value: "UAH", label: "Ukrainian Hryvnia", symbol: "₴", region: "Europe" },
  { value: "BTC", label: "Bitcoin", symbol: "₿", region: "Crypto" },
  { value: "ETH", label: "Ethereum", symbol: "Ξ", region: "Crypto" },
];

export default function Settings({ user, setUser }) {
  const [currency, setCurrency] = useState(user?.default_currency || "EUR");
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Cashflow timing
  const [salaryDay, setSalaryDay] = useState(user?.salary_day_of_month || 1);
  const [bonusMonth, setBonusMonth] = useState(user?.annual_bonus_month || 12);
  const [savingTiming, setSavingTiming] = useState(false);
  
  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Check if user has password-based auth (has username)
  const hasPasswordAuth = user?.username || user?.has_password;

  const filteredCurrencies = useMemo(() => {
    if (!searchQuery) return CURRENCIES;
    const query = searchQuery.toLowerCase();
    return CURRENCIES.filter(
      (c) =>
        c.value.toLowerCase().includes(query) ||
        c.label.toLowerCase().includes(query) ||
        c.symbol.toLowerCase().includes(query) ||
        c.region.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  const selectedCurrency = CURRENCIES.find((c) => c.value === currency);

  // Password validation
  const passwordValidation = useMemo(() => {
    const checks = {
      length: newPassword.length >= 8,
      uppercase: /[A-Z]/.test(newPassword),
      lowercase: /[a-z]/.test(newPassword),
      number: /[0-9]/.test(newPassword),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
    };
    const isValid = Object.values(checks).every(Boolean);
    const matches = newPassword === confirmPassword && confirmPassword !== "";
    return { checks, isValid, matches };
  }, [newPassword, confirmPassword]);

  const handleChangePassword = async () => {
    if (!passwordValidation.isValid) {
      toast.error("Password doesn't meet requirements");
      return;
    }
    if (!passwordValidation.matches) {
      toast.error("Passwords don't match");
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password changed successfully");
      setShowPasswordForm(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to change password";
      toast.error(message);
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSaveCurrency = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/currency`, { default_currency: currency });
      setUser({ ...user, default_currency: currency });
      toast.success("Currency updated successfully");
    } catch (error) {
      console.error("Error updating currency:", error);
      toast.error("Failed to update currency");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTiming = async () => {
    setSavingTiming(true);
    try {
      await axios.put(`${API}/settings/cashflow-timing`, {
        salary_day_of_month: Number(salaryDay),
        annual_bonus_month: Number(bonusMonth),
      });
      setUser({
        ...user,
        salary_day_of_month: Number(salaryDay),
        annual_bonus_month: Number(bonusMonth),
      });
      toast.success("Cashflow timing updated");
    } catch (error) {
      const msg = error?.response?.data?.detail || "Failed to update";
      toast.error(typeof msg === "string" ? msg : "Failed to update");
    } finally {
      setSavingTiming(false);
    }
  };

  const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const timingChanged =
    Number(salaryDay) !== (user?.salary_day_of_month || 1) ||
    Number(bonusMonth) !== (user?.annual_bonus_month || 12);

  return (
    <Layout user={user} pageTitle="Settings">
      <div data-testid="settings-page" className="space-y-8 max-w-2xl">
        {/* Currency Settings */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-card border-border">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <CardTitle className="text-lg font-bold text-white">Currency Settings</CardTitle>
                  <CardDescription className="text-gray-400">Choose your default currency for the app</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Current Selection */}
              <div className="p-4 bg-secondary rounded-xl border border-border">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Current Default</p>
                <p className="text-2xl font-bold text-green-400">
                  {selectedCurrency?.symbol} <span className="text-white">{selectedCurrency?.label}</span>
                </p>
                <p className="text-sm text-gray-500">{selectedCurrency?.value}</p>
              </div>

              {/* Search */}
              <div>
                <Label className="text-gray-300">Search Currency</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name, code, or region..."
                    className="pl-10 bg-secondary border-border text-white placeholder:text-gray-500"
                    data-testid="currency-search"
                  />
                </div>
              </div>

              {/* Currency List */}
              <div>
                <Label className="text-gray-300">Select Currency ({filteredCurrencies.length} available)</Label>
                <ScrollArea className="h-64 mt-2 rounded-xl border border-border">
                  <div className="p-2 space-y-1">
                    {filteredCurrencies.map((curr) => (
                      <button
                        key={curr.value}
                        onClick={() => setCurrency(curr.value)}
                        data-testid={`currency-option-${curr.value}`}
                        className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-all ${
                          currency === curr.value
                            ? "bg-green-500/20 border border-green-500/50 text-white"
                            : "hover:bg-secondary text-gray-300 hover:text-white"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xl font-mono w-10">{curr.symbol}</span>
                          <div>
                            <p className="font-medium">{curr.label}</p>
                            <p className="text-xs text-gray-500">{curr.value} • {curr.region}</p>
                          </div>
                        </div>
                        {currency === curr.value && (
                          <Check className="w-5 h-5 text-green-400" />
                        )}
                      </button>
                    ))}
                    {filteredCurrencies.length === 0 && (
                      <p className="text-center py-8 text-gray-500">No currencies found</p>
                    )}
                  </div>
                </ScrollArea>
              </div>

              <p className="text-sm text-gray-500">
                This will be used as the default currency across the app. You can still use different currencies for individual accounts.
              </p>

              <Button
                onClick={handleSaveCurrency}
                disabled={saving || currency === user?.default_currency}
                className="bg-green-500 hover:bg-green-400 text-black rounded-full px-6 font-semibold"
                data-testid="save-currency-btn"
              >
                {saving ? (
                  "Saving..."
                ) : currency === user?.default_currency ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Saved
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        {/* Cashflow Timing */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
          <Card className="bg-card border-border">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <CalendarClock className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <CardTitle className="text-lg font-bold text-white">Cashflow Timing</CardTitle>
                  <CardDescription className="text-gray-400">
                    Used by forecasts to skip months whose salary/bonus has already been paid this year.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Salary day of month</Label>
                  <Select
                    value={String(salaryDay)}
                    onValueChange={(v) => setSalaryDay(Number(v))}
                  >
                    <SelectTrigger
                      className="mt-1 bg-secondary border-border text-white"
                      data-testid="salary-day-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border max-h-72">
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                        <SelectItem
                          key={d}
                          value={String(d)}
                          className="text-gray-300 focus:text-white focus:bg-secondary"
                        >
                          {d}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Current month counts only if today &le; day {salaryDay}.
                  </p>
                </div>
                <div>
                  <Label className="text-gray-300">Annual bonus month</Label>
                  <Select
                    value={String(bonusMonth)}
                    onValueChange={(v) => setBonusMonth(Number(v))}
                  >
                    <SelectTrigger
                      className="mt-1 bg-secondary border-border text-white"
                      data-testid="bonus-month-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border max-h-72">
                      {MONTHS.map((m, i) => (
                        <SelectItem
                          key={m}
                          value={String(i + 1)}
                          className="text-gray-300 focus:text-white focus:bg-secondary"
                        >
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Annual savings drop from this year&apos;s forecast once this month has passed.
                  </p>
                </div>
              </div>
              <Button
                onClick={handleSaveTiming}
                disabled={savingTiming || !timingChanged}
                className="bg-amber-500 hover:bg-amber-400 text-black rounded-full px-6 font-semibold"
                data-testid="save-timing-btn"
              >
                {savingTiming ? "Saving..." : timingChanged ? "Save timing" : (<><Check className="w-4 h-4 mr-2" />Saved</>)}
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        {/* Profile Info */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="bg-card border-border">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                  <SettingsIcon className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <CardTitle className="text-lg font-bold text-white">Profile Information</CardTitle>
                  <CardDescription className="text-gray-400">
                    {hasPasswordAuth ? "Your account details" : "Your account details from Google"}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-secondary rounded-xl border border-border">
                  {user?.picture && (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="w-16 h-16 rounded-full border-2 border-green-500/30"
                    />
                  )}
                  {!user?.picture && (
                    <div className="w-16 h-16 rounded-full border-2 border-green-500/30 bg-green-500/10 flex items-center justify-center">
                      <span className="text-2xl font-bold text-green-400">
                        {user?.name?.charAt(0)?.toUpperCase() || user?.username?.charAt(0)?.toUpperCase() || "U"}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="font-bold text-white">{user?.name}</p>
                    <p className="text-gray-400">{user?.email}</p>
                    {user?.username && (
                      <p className="text-sm text-gray-500">@{user.username}</p>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-500">
                  {hasPasswordAuth 
                    ? "You can update your password below."
                    : "Profile information is managed through your Google account. To update your name or picture, please update your Google profile."}
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Change Password Section - Only for password-based auth users */}
        {hasPasswordAuth && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="bg-card border-border">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center">
                    <Lock className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-bold text-white">Security</CardTitle>
                    <CardDescription className="text-gray-400">Change your account password</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {!showPasswordForm ? (
                  <Button
                    onClick={() => setShowPasswordForm(true)}
                    variant="outline"
                    className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10"
                    data-testid="change-password-btn"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Change Password
                  </Button>
                ) : (
                  <div className="space-y-4">
                    {/* Current Password */}
                    <div>
                      <Label className="text-gray-300">Current Password</Label>
                      <div className="relative mt-1">
                        <Input
                          type={showCurrentPassword ? "text" : "password"}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="bg-secondary border-border text-white pr-10"
                          placeholder="Enter current password"
                          data-testid="current-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                        >
                          {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    {/* New Password */}
                    <div>
                      <Label className="text-gray-300">New Password</Label>
                      <div className="relative mt-1">
                        <Input
                          type={showNewPassword ? "text" : "password"}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="bg-secondary border-border text-white pr-10"
                          placeholder="Enter new password"
                          data-testid="new-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                        >
                          {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      
                      {/* Password Requirements */}
                      {newPassword && (
                        <div className="mt-2 space-y-1">
                          {[
                            { key: "length", label: "At least 8 characters" },
                            { key: "uppercase", label: "One uppercase letter" },
                            { key: "lowercase", label: "One lowercase letter" },
                            { key: "number", label: "One number" },
                            { key: "special", label: "One special character" },
                          ].map(({ key, label }) => (
                            <div key={key} className="flex items-center gap-2 text-xs">
                              {passwordValidation.checks[key] ? (
                                <Check className="w-3 h-3 text-green-400" />
                              ) : (
                                <X className="w-3 h-3 text-gray-500" />
                              )}
                              <span className={passwordValidation.checks[key] ? "text-green-400" : "text-gray-500"}>
                                {label}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Confirm Password */}
                    <div>
                      <Label className="text-gray-300">Confirm New Password</Label>
                      <Input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="bg-secondary border-border text-white mt-1"
                        placeholder="Confirm new password"
                        data-testid="confirm-password-input"
                      />
                      {confirmPassword && (
                        <div className="mt-1 flex items-center gap-2 text-xs">
                          {passwordValidation.matches ? (
                            <>
                              <Check className="w-3 h-3 text-green-400" />
                              <span className="text-green-400">Passwords match</span>
                            </>
                          ) : (
                            <>
                              <X className="w-3 h-3 text-red-400" />
                              <span className="text-red-400">Passwords don't match</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-2">
                      <Button
                        onClick={handleChangePassword}
                        disabled={changingPassword || !passwordValidation.isValid || !passwordValidation.matches || !currentPassword}
                        className="bg-orange-500 hover:bg-orange-400 text-black font-semibold"
                        data-testid="submit-password-change-btn"
                      >
                        {changingPassword ? "Changing..." : "Change Password"}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowPasswordForm(false);
                          setCurrentPassword("");
                          setNewPassword("");
                          setConfirmPassword("");
                        }}
                        className="border-gray-600 text-gray-300 hover:bg-gray-800"
                        data-testid="cancel-password-change-btn"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </Layout>
  );
}
